import React, { FC } from 'react';
import { useTranslation, useTrackElement } from '@edreamsodigeo/frontend-core';
import { Flex, Button, Box } from '@edreamsodigeo/prisma-design-system';
import { AlternativeItineraryValuePropositionType } from 'clientState/providers/AlternativeItineraryProvider/types';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';
import { useFlowTools } from 'flowTools/useFlowTools';
import { Money } from 'generated/graphql';
import { useAlternativeItinerary } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';

export type AlternativeItineraryLayerButtonsProps = {
  itinerary?: ThreeColumnsItineraryType;
  valueProposition: AlternativeItineraryValuePropositionType;
  discountAmount?: Money | undefined;
  selectedItinerary: ThreeColumnsItineraryType | undefined;
  nonIssuable?: boolean;
  showContinueButton?: boolean;
  onContinue: () => void;
  onChange: () => void;
};

const AlternativeItineraryDoubleCardLayerButtonsMobile: FC<
  AlternativeItineraryLayerButtonsProps
> = ({
  itinerary,
  selectedItinerary,
  nonIssuable,
  showContinueButton = false,
  valueProposition,
  discountAmount,
  onContinue,
  onChange,
}) => {
  const { t } = useTranslation();
  const { altSearchId } = useAlternativeItinerary();
  const changeButtonKeySuffix =
    valueProposition === 'price' || valueProposition === 'discount'
      ? 'price'
      : 'similar';
  const changeButtonKey = nonIssuable
    ? 'alternativeItinerary.nonIssuable.button.change'
    : `alternativeItinerary.issuable.button.change.${changeButtonKeySuffix}`;
  const continueButtonKey = showContinueButton
    ? 'modal.availability.button'
    : 'alternativeItinerary.issuable.button.continue.similar';

  const changeTrackingRef = useTrackElement(
    ['click'],
    `alternative-flights-change-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}-discount:${discountAmount?.amount}${discountAmount?.currency}`
  );

  const continueTrackingRef = useTrackElement(
    ['click'],
    `alternative-flights-continue-original-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}`
  );

  return (
    <Flex flexDirection="column" alignItems="center" pt={'1x_big'}>
      <div
        ref={changeTrackingRef}
        style={{ width: '100%' }}
        data-testid="change-button-mobile"
        onClick={onChange}
      >
        <Button
          type="primary"
          size="large"
          sx={{ fontWeight: 'medium' }}
          fullWidth={true}
        >
          {t(changeButtonKey)}
        </Button>
      </div>
      {(!nonIssuable || showContinueButton) && (
        <Box mt="base" size="100%">
          <div
            ref={continueTrackingRef}
            data-testid="continue-button-mobile"
            onClick={onContinue}
          >
            <Button type="text" size="large" fullWidth={true}>
              {t(continueButtonKey)}
            </Button>
          </div>
        </Box>
      )}
    </Flex>
  );
};

const AlternativeItineraryDoubleCardLayerButtonsDesktop: FC<
  AlternativeItineraryLayerButtonsProps
> = ({
  itinerary,
  selectedItinerary,
  valueProposition,
  discountAmount,
  nonIssuable,
  onContinue,
  onChange,
}) => {
  const { t } = useTranslation();
  const { altSearchId } = useAlternativeItinerary();
  const changeButtonKeySuffix =
    valueProposition === 'price' || valueProposition === 'discount'
      ? 'price'
      : 'similar';
  const changeButtonKey = `alternativeItinerary.issuable.button.change.${changeButtonKeySuffix}`;
  const continueButtonKey = nonIssuable
    ? 'modal.availability.button'
    : 'alternativeItinerary.issuable.button.continue.similar';

  const changeTrackingRef = useTrackElement(
    ['click'],
    `alternative-flights-change-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}-discount:${discountAmount?.amount}${discountAmount?.currency}`
  );

  const continueTracking = useTrackElement(
    ['click'],
    `alternative-flights-continue-original-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}`
  );

  const continueTrackingRef = !nonIssuable ? continueTracking : null;

  return (
    <Flex flexDirection="column" alignItems="center">
      <div
        ref={changeTrackingRef}
        style={{ width: '100%' }}
        onClick={onChange}
        data-testid="change-button-desktop"
      >
        <Button
          type="primary"
          size="large"
          sx={{ fontWeight: 'medium' }}
          fullWidth={true}
        >
          {t(changeButtonKey)}
        </Button>
      </div>
      <Box size="100%" paddingTop="1x_small">
        <div
          ref={continueTrackingRef}
          onClick={onContinue}
          data-testid="continue-button-desktop"
        >
          <Button type="text" size="large" fullWidth={true}>
            {t(continueButtonKey)}
          </Button>
        </div>
      </Box>
    </Flex>
  );
};

const AlternativeItineraryDoubleCardLayerButtonsComponent: FC<
  AlternativeItineraryLayerButtonsProps
> = (props) => {
  const { isMobile } = useFlowTools();
  const Component = isMobile
    ? AlternativeItineraryDoubleCardLayerButtonsMobile
    : AlternativeItineraryDoubleCardLayerButtonsDesktop;
  return <Component {...props} />;
};

export default AlternativeItineraryDoubleCardLayerButtonsComponent;
